<template>
  <div>
    <PageHeader title="สถานะการประมวลผลวิดีโอ" />
    <b-container fluid="xl">
      <div class="py-5">
        <b-row class="mb-3" align-v="end">
          <b-col md="4" class="mb-3 mb-md-0">
            <label class="d-block">
              รหัสคอร์ส
            </label>
            <b-form-input
              v-model="filters.courseCode"
              placeholder="ค้นหารหัสคอร์ส"
              @keyup.enter="fetchVideoProcessData(1)"
            ></b-form-input>
          </b-col>
          <b-col md="4" class="mb-3 mb-md-0">
            <label class="d-block">
              ชื่อคอร์สเรียน
            </label>
            <b-form-input
              v-model="filters.courseName"
              placeholder="ค้นหาชื่อคอร์สเรียน"
              @keyup.enter="fetchVideoProcessData(1)"
            ></b-form-input>
          </b-col>
          <b-col md="4" class="mb-3 mb-md-0">
            <label class="d-block">
              สถานะ
            </label>
            <b-form-select
              v-model="filters.status"
              :options="statusOptions"
            ></b-form-select>
          </b-col>
        </b-row>

        <!-- ตัวกรองแถวที่ 2 คือ วันที่และวันที่ต้องการค้นหา -->
        <b-row class="mb-3" align-v="end">
          <b-col md="4" class="mb-3 mb-md-0">
            <label class="d-block">
              วันที่
            </label>
            <b-form-select
              v-model="filters.dateType"
              :options="dateTypeOptions"
            ></b-form-select>
          </b-col>
          <b-col md="4" class="mb-3 mb-md-0">
            <label class="d-block">
              วันที่ต้องการค้นหา
            </label>
            <div class="date-range-field">
              <b-input-group>
                <b-form-input
                  type="text"
                  :value="formatDateRange"
                  placeholder="เลือกช่วงวันที่"
                  disabled
                />
                <b-input-group-append>
                  <b-button
                    variant="secondary"
                    @click="showCalendarRange('video-process-date')"
                  >
                    <b-icon icon="calendar-range"></b-icon>
                  </b-button>
                </b-input-group-append>
              </b-input-group>
              <DatePicker
                v-model="filters.dateRange"
                locale="th"
                id="video-process-date"
                style="display: none;"
                is-range
              />
            </div>
          </b-col>
        </b-row>

        <b-row class="mb-4">
          <b-col cols="6" md="3" lg="1">
            <b-button
              variant="primary"
              block
              class="font-weight-normal"
              @click="fetchVideoProcessData(1)"
            >
              ค้นหา
            </b-button>
          </b-col>
        </b-row>

        <!-- ตารางแสดงสถานะการประมวลผลวิดีโอ -->
        <b-table
          :fields="tableFields"
          :items="videoProcessList.data"
          id="video-process-list"
          :busy="isLoading"
          :per-page="perPage"
          responsive
          sort-icon-left
          striped
          table-class="mt-4 border-bottom"
          show-empty
        >
          <template #empty>
            <div class="text-center py-3 text-muted">ไม่พบข้อมูล</div>
          </template>

          <template #table-busy>
            <div class="text-center text-danger my-4">
              <Loading />
            </div>
          </template>

          <!-- รหัสคอร์ส -->
          <template #cell(c_code)="props">
            {{ props.item.c_code || "-" }}
          </template>

          <!-- ชื่อคอร์สเรียน -->
          <template #cell(c_name)="props">
            {{ props.item.c_name || "-" }}
          </template>

          <!-- ชื่อบทเรียน -->
          <template #cell(lec_name)="props">
            {{ props.item.lec_name || "-" }}
          </template>

          <!-- วันที่อัปโหลด -->
          <template #cell(uploaded_at)="props">
            <div v-if="props.item.uploaded_at">
              <span class="d-block">
                {{ formatThaiDate(props.item.uploaded_at).date }}
              </span>
              <span class="d-block">
                {{ formatThaiDate(props.item.uploaded_at).time }}
              </span>
            </div>
            <span v-else>-</span>
          </template>

          <!-- วันที่เริ่ม -->
          <template #cell(started_at)="props">
            <div v-if="props.item.started_at">
              <span class="d-block">
                {{ formatThaiDate(props.item.started_at).date }}
              </span>
              <span class="d-block">
                {{ formatThaiDate(props.item.started_at).time }}
              </span>
            </div>
            <span v-else>-</span>
          </template>

          <!-- วันที่เสร็จ -->
          <template #cell(finished_at)="props">
            <div v-if="props.item.finished_at">
              <span class="d-block">
                {{ formatThaiDate(props.item.finished_at).date }}
              </span>
              <span class="d-block">
                {{ formatThaiDate(props.item.finished_at).time }}
              </span>
            </div>
            <span v-else>-</span>
          </template>

          <!-- สถานะ -->
          <template #cell(status)="props">
            <b-badge :variant="getStatusBadge(props.item.status).variant">
              {{ getStatusBadge(props.item.status).text }}
            </b-badge>
          </template>

          <!-- Reset -->
          <template #cell(reset)="props">
            <b-button
              variant="outline-primary"
              size="sm"
              :to="resetRouteTo(props.item)"
              @click="onResetClick(props.item, $event)"
            >
              Reset
            </b-button>
          </template>
        </b-table>

        <div class="row mt-3">
          <div class="col-lg-6">
            <form class="form-inline">
              <div class="form-group mb-2">
                <label>แสดง</label>
              </div>
              <div class="form-group ml-2 mb-2">
                <b-form-select
                  v-model="perPage"
                  :options="[10, 15, 30, 50]"
                  size="sm"
                  @input="setFetchParamsPerPage"
                ></b-form-select>
              </div>
              <div class="form-group ml-2 mb-2">
                <label>
                  จาก
                  {{ Number(videoProcessList.total).toLocaleString() }}
                  รายการ
                </label>
              </div>
            </form>
          </div>
          <div class="col-lg-6">
            <nav class="float-right" aria-label="Page navigation example">
              <b-pagination
                v-model="currentPage"
                :total-rows="videoProcessList.total"
                :per-page="perPage"
                @change="setFetchParamsPage"
                aria-controls="video-process-list"
              ></b-pagination>
            </nav>
          </div>
        </div>
      </div>
    </b-container>
  </div>
</template>

<script>
import PageHeader from "@/components/layouts/PageHeader";
import Loading from "@/components/shared/Loading";
import DatePicker from "v-calendar/lib/components/date-picker.umd";
import moment from "moment";
import api from "@/services/api.js";

export default {
  name: "VideoProcessStatusPage",
  components: { PageHeader, Loading, DatePicker },
  data() {
    return {
      isLoading: false,
      perPage: 10,
      currentPage: 1,
      filters: {
        courseCode: "",
        courseName: "",
        status: null,
        dateType: "uploaded_at",
        // ค่าเริ่มต้นค้นหาย้อนหลัง 1 สัปดาห์
        dateRange: {
          start: moment()
            .subtract(7, "days")
            .toDate(),
          end: moment().toDate()
        }
      },
      statusOptions: [
        { value: null, text: "สถานะทั้งหมด" },
        { value: "PENDING", text: "รอประมวลผล" },
        { value: "PROCESSING", text: "กำลังประมวลผล" },
        { value: "COMPLETED", text: "ประมวลผลสำเร็จ" },
        { value: "FAILED", text: "ไม่สำเร็จ" }
      ],
      dateTypeOptions: [
        { value: "uploaded_at", text: "วันที่อัปโหลด" },
        { value: "started_at", text: "วันที่เริ่ม" },
        { value: "finished_at", text: "วันที่เสร็จ" }
      ],
      videoProcessList: {
        data: [],
        total: 0
      },
      tableFields: [
        {
          key: "c_code",
          label: "รหัสคอร์ส",
          sortable: true,
          thStyle: { width: "8%" }
        },
        {
          key: "c_name",
          label: "ชื่อคอร์สเรียน",
          sortable: true,
          thStyle: { width: "24%" }
        },
        {
          key: "lec_name",
          label: "ชื่อบทเรียน",
          sortable: true,
          thStyle: { width: "24%" }
        },
        {
          key: "uploaded_at",
          label: "วันที่อัปโหลด",
          sortable: true,
          thStyle: { width: "10%" }
        },
        {
          key: "started_at",
          label: "วันที่เริ่ม",
          sortable: true,
          thStyle: { width: "10%" }
        },
        {
          key: "finished_at",
          label: "วันที่เสร็จ",
          sortable: true,
          thStyle: { width: "10%" }
        },
        {
          key: "status",
          label: "สถานะ",
          sortable: true,
          thStyle: { width: "8%" }
        },
        {
          key: "reset",
          label: "Reset",
          sortable: false,
          thStyle: { width: "6%" }
        }
      ]
    };
  },
  computed: {
    formatDateRange() {
      const { start, end } = this.filters.dateRange;
      if (!start || !end) return "";
      return `${moment(start).format("DD/MM/YYYY")} - ${moment(end).format(
        "DD/MM/YYYY"
      )}`;
    }
  },
  mounted() {
    this.fetchVideoProcessData();
  },
  methods: {
    async fetchVideoProcessData(page = this.currentPage) {
      this.isLoading = true;
      this.currentPage = page;

      try {
        const response = await api.get("/v1/video-process-logs", {
          params: {
            page: this.currentPage,
            limit: this.perPage,
            course_code: this.filters.courseCode || null,
            course_name: this.filters.courseName || null,
            status: this.filters.status,
            date_type: this.filters.dateType,
            start_date: this.filters.dateRange.start
              ? moment(this.filters.dateRange.start).format("YYYY-MM-DD")
              : null,
            end_date: this.filters.dateRange.end
              ? moment(this.filters.dateRange.end).format("YYYY-MM-DD")
              : null
          },
          headers: {
            ComId: this.$store.state.Company.companyInfo.id
          }
        });

        this.videoProcessList.data = response.data.data || [];
        this.videoProcessList.total = response.data.total || 0;
      } catch (error) {
        console.error("Error fetching video process data:", error);
        this.videoProcessList.data = [];
        this.videoProcessList.total = 0;
      } finally {
        this.isLoading = false;
      }
    },

    // Create target route for reset button to be able to open with new tab when clicking middle button to the reset button
    resetRouteTo(item) {
      return item.c_id
        ? {
            name: "course.management.contents",
            params: { courseId: String(item.c_id) }
          }
        : null;
    },

    onResetClick(item) {
      if (item.c_id) return;
      this.$bvToast.toast("ไม่พบข้อมูลคอร์สของรายการนี้", {
        title: "แจ้งเตือน",
        variant: "warning",
        solid: true
      });
    },

    getStatusBadge(status) {
      const statusMap = {
        PENDING: { text: "รอประมวลผล", variant: "secondary" },
        PROCESSING: { text: "กำลังประมวลผล", variant: "warning" },
        COMPLETED: { text: "ประมวลผลสำเร็จ", variant: "success" },
        FAILED: { text: "ไม่สำเร็จ", variant: "danger" }
      };
      return statusMap[status] || { text: status || "-", variant: "light" };
    },

    formatThaiDate(dateString) {
      if (!dateString || dateString === "-") return { date: "-", time: "" };
      const date = new Date(dateString);
      const datePart = date.toLocaleDateString("th-TH", {
        year: "numeric",
        month: "short",
        day: "numeric"
      });
      const timePart = date.toLocaleTimeString("th-TH", {
        hour: "2-digit",
        minute: "2-digit"
      });
      return { date: datePart, time: timePart };
    },

    showCalendarRange(calendarId) {
      let rangeCalendar = document.getElementById(calendarId);
      if (rangeCalendar.style.display == "inline-block") {
        rangeCalendar.style.display = "none";
      } else {
        rangeCalendar.style.display = "inline-block";
      }
    },

    setFetchParamsPage(page) {
      this.fetchVideoProcessData(page);
    },

    setFetchParamsPerPage() {
      this.fetchVideoProcessData(1);
    }
  }
};
</script>

<style scoped>
::v-deep #video-process-list thead th {
  text-align: center;
  vertical-align: middle;
  white-space: nowrap;
}

.date-range-field {
  position: relative;
}

::v-deep #video-process-date.vc-container {
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 1000;
  background-color: #fff;
  border-radius: 0.375rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

::v-deep #video-process-date .vc-container {
  background-color: #fff;
}
</style>
